import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import * as React from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { deepEqual } from 'jamespot-user-api';
import { DataCy } from '../../../../types/dataAttributes';
import { JRCInputFieldProps } from '../Common/JRCFormFieldRenderer.types';
import { CheckboxValue } from './JRCInputCheckbox.types';
import { RefCallBack } from 'react-hook-form';
import { FieldValues } from 'react-hook-form';
import { getNewValue } from './JRCInputCheckbox.utils';
import styled from 'styled-components';
import { BETA_JRCDragAndDrop } from 'components/Beta/BETA_JRCDragAndDrop/BETA_JRCDragAndDrop';
import { arraySwapElements } from 'utils/utils.array';
import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import { CheckboxOrToggle } from '../Deprecated/JRCFormCheckbox/JRCFormCheckbox.style';
import { ReadOnlyBorder, ReadOnlyUl } from './JRCInputCheckbox';

type CheckboxOption<TFieldValues> = {
    label: string | JSX.Element;
    value: string | number | TFieldValues;
};

export type CheckboxProps<TFieldValues> = DataCy & {
    options: Array<CheckboxOption<TFieldValues>>;
    readOnly?: boolean;
    getLabel?: (option: CheckboxOption<TFieldValues>) => React.ReactNode;
    canReorder?: boolean;
};

export type JRCInputCheckboxProps<TFieldValues extends FieldValues = FieldValues> = CheckboxProps<TFieldValues> &
    JRCInputFieldProps<TFieldValues>;
export type NativeCheckboxProps<TFieldValues extends FieldValues = FieldValues> = CheckboxProps<TFieldValues> &
    Omit<ControllerRenderProps<TFieldValues>, 'value'> & { value: TFieldValues[] };

const Option = styled.label<{ canReorder?: boolean; selected?: boolean }>`
    display: flex;
    align-items: center;
    padding: ${(props) => (!props.canReorder || props.selected ? '8px' : '8px 8px 8px 24px')};
    border-radius: 8px;
    border: 1px solid ${(props) => props.theme.color.grey2};
    ${(props) => props.selected && `background-color: ${props.theme.color.grey0}`};
    margin: 8px 0;
    font-size: 16px;
    line-height: 24px;
    cursor: pointer;
`;

const Flex = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
`;

const RenderInputElement = function <TFieldValues extends FieldValues = FieldValues>(
    { getLabel = (option) => option.label, canReorder = true, ...props }: NativeCheckboxProps<TFieldValues>,
    ref: React.ForwardedRef<HTMLInputElement>,
) {
    const field = {
        onChange: props.onChange,
        onBlur: props.onBlur,
        value: props.value,
        name: props.name,
        ref: ref as RefCallBack,
    };

    const isChecked = (
        field: Omit<ControllerRenderProps<TFieldValues>, 'value'> & { value: TFieldValues | TFieldValues[] },
        option: CheckboxOption<TFieldValues>,
    ): boolean => field && (field.value as CheckboxValue<TFieldValues>[])?.some((v) => deepEqual(v, option.value));

    function getOptionLabel(value: TFieldValues) {
        return props.options.find((option) => option.value === value)?.label;
    }

    const selectedOptions = React.useMemo(
        () =>
            field?.value
                ?.map((value) => props.options.find((option) => deepEqual(option.value, value)))
                .filter((o): o is CheckboxOption<TFieldValues> => !!o),
        [field.value, props.options],
    );

    return (
        <>
            {props.readOnly ? (
                <ReadOnlyBorder>
                    <ReadOnlyUl>
                        {field.value?.map((value: TFieldValues, index) => (
                            <li key={index}>{getOptionLabel(value)}</li>
                        ))}
                    </ReadOnlyUl>
                </ReadOnlyBorder>
            ) : (
                <>
                    {canReorder ? (
                        <BETA_JRCDragAndDrop
                            elms={selectedOptions}
                            renderEl={(option) => (
                                <Option selected>
                                    <JRCIcon name="icon-fs-dw" color="grey" size={12} />
                                    <CheckboxOrToggle
                                        checkboxMode="toggle"
                                        checked
                                        onChange={() => field.onChange(getNewValue(field?.value, option.value, false))}
                                    />
                                    <Flex>{getLabel(option)}</Flex>
                                </Option>
                            )}
                            onDrag={(dragIndex, hoverIndex) => {
                                if (field?.value !== undefined) {
                                    const newValue = arraySwapElements(field.value, dragIndex, hoverIndex);

                                    field.onChange(newValue);
                                }
                            }}
                        />
                    ) : (
                        selectedOptions.map((option, index) => (
                            <Option key={index} selected>
                                <CheckboxOrToggle
                                    checkboxMode="toggle"
                                    checked
                                    onChange={() => field.onChange(getNewValue(field?.value, option.value, false))}
                                />
                                <Flex>{getLabel(option)}</Flex>
                            </Option>
                        ))
                    )}
                    {props.options
                        .filter((option) => !isChecked(field, option))
                        .map((option, index) => (
                            <Option key={index} canReorder={canReorder}>
                                <CheckboxOrToggle
                                    checkboxMode="toggle"
                                    checked={false}
                                    onChange={() => field.onChange(getNewValue(field?.value, option.value, true))}
                                />
                                {getLabel(option)}
                            </Option>
                        ))}
                </>
            )}
        </>
    );
};
const RenderInput = React.forwardRef(RenderInputElement) as unknown as <T extends FieldValues = FieldValues>(
    props: NativeCheckboxProps<T>,
) => JSX.Element;

export function JRCInputReorderCheckbox<TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    rules,
    label,
    htmlFor,
    labelIsLegend,
    description,
    helper,
    margin,
    ...props
}: JRCInputCheckboxProps<TFieldValues>) {
    return (
        <JRCFormFieldRenderer
            name={name}
            control={control}
            rules={rules}
            label={label}
            htmlFor={htmlFor}
            labelIsLegend={labelIsLegend || true}
            description={description}
            helper={helper}
            margin={margin}
            renderFunction={({ value, ...field }) => <RenderInput {...props} {...field} value={value} />}
        />
    );
}
